import React from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, Typography, Avatar, IconButton } from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Business as SitesIcon,
    Assessment as ReportsIcon,
    People as UsersIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    CreditCard as CardIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isLawEnforcementPlan } from '../../utils/subscriptionCategory';

const SidebarContainer = styled(Box)(({ theme }) => ({
    width: '260px',
    position: 'fixed',
    left: '24px',
    top: '24px',
    height: 'calc(100vh - 48px)',
    /* Card-4 style: same as .card-4 */
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(34, 211, 238, 0.25)',
    borderTop: '1px solid rgba(34, 211, 238, 0.5)',
    borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
    borderRight: '1px solid rgba(34, 211, 238, 0.25)',
    borderBottom: '1px solid rgba(34, 211, 238, 0.25)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '24px',
    paddingLeft: '24px',
    paddingBottom: '24px',
    paddingRight: '24px',
    zIndex: 1200,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(0, 0, 0, 0.1)',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.text.secondary,
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#22d3ee',
    },
}));

const LogoContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    marginBottom: '40px',
    justifyContent: 'center',
    '& img': {
        height: '40px',
        maxWidth: '100%',
        objectFit: 'contain',
        filter: 'brightness(0) invert(1)',
    },
});

const NavItem = styled(ListItem)<{ active?: boolean }>(({ active, theme }) => ({
    borderRadius: '8px',
    marginBottom: '8px',
    padding: '12px 16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: active ? 'rgba(34, 211, 238, 0.2)' : 'transparent',
    border: active ? '1px solid rgba(34, 211, 238, 0.3)' : '1px solid transparent',
    boxShadow: active ? 'inset 0 0 12px rgba(34, 211, 238, 0.1)' : 'none',

    '&:hover': {
        backgroundColor: active ? 'rgba(34, 211, 238, 0.25)' : 'rgba(255, 255, 255, 0.05)',
        color: theme.palette.text.primary,
    },

    '& .MuiListItemIcon-root': {
        minWidth: '40px',
        color: active ? '#22d3ee' : theme.palette.text.secondary,
        transition: 'all 0.2s ease',
    },

    '& .MuiListItemText-primary': {
        fontFamily: "'Inter', sans-serif",
        fontSize: '14px',
        fontWeight: active ? 600 : 500,
        color: active ? theme.palette.text.primary : theme.palette.text.secondary,
        transition: 'all 0.2s ease',
    },

    '&:hover .MuiListItemIcon-root': {
        color: '#22d3ee',
    },
    '&:hover .MuiListItemText-primary': {
        color: theme.palette.text.primary,
    },
}));

const UserProfileSection = styled(Box)({
    marginTop: 'auto',
    paddingTop: '20px',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

interface SideNavigationBarProps {
    onNavClick?: (path: string) => void;
}

const SideNavigationBar: React.FC<SideNavigationBarProps> = ({ onNavClick }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userProfile, logout } = useAuth();

    const handleNavigation = (path: string) => {
        navigate(path);
        if (onNavClick) onNavClick(path);
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const isAdmin = userProfile?.role === 'Admin';
    const sitesLabel = isLawEnforcementPlan(userProfile?.subscriptionPlan) ? 'Sweeps' : 'Sites & Locations';

    const navItems = [
        { label: 'Feed', path: '/profile', icon: <DashboardIcon />, exact: true },
        { label: 'Reports', path: '/reports', icon: <ReportsIcon /> }, // Assuming we route these eventually, or maintain them via state
        { label: sitesLabel, path: '/sites', icon: <SitesIcon /> },
        { label: 'Card Reader Profiles', path: '/profiles', icon: <CardIcon /> },
        ...(isAdmin ? [{ label: 'User Management', path: '/users', icon: <UsersIcon /> }] : []),
        { label: 'Platform Settings', path: '/settings', icon: <SettingsIcon /> },
    ];

    // Logic to determine active state; simple matching for now until React Router is fully rigged for the new layout
    const isActive = (path: string, exact?: boolean) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    return (
        <SidebarContainer>
            <LogoContainer>
                <img src="/SkimBusterOnlyBannerNew.svg" alt="SkimBuster Logo" />
            </LogoContainer>

            <Typography variant="overline" sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', letterSpacing: '1px', marginBottom: 1.5, display: 'block', textTransform: 'uppercase' }}>
                MAIN MENU
            </Typography>

            <List sx={{ px: 0 }}>
                {navItems.map((item, index) => (
                    <NavItem
                        key={index}
                        active={isActive(item.path, item.exact)}
                        onClick={() => handleNavigation(item.path)}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.label} />
                    </NavItem>
                ))}
            </List>

            <UserProfileSection>
                <Box display="flex" alignItems="center" gap={2}>
                    <Avatar
                        sx={{
                            bgcolor: 'rgba(34, 211, 238, 0.2)',
                            color: '#22d3ee',
                            border: '1px solid rgba(34, 211, 238, 0.5)',
                            boxShadow: '0 0 10px rgba(34, 211, 238, 0.2)'
                        }}
                    >
                        {userProfile?.name?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                            {userProfile?.name || 'User'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {userProfile?.role || 'User'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={handleLogout} sx={{ color: '#ef4444', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' } }}>
                    <LogoutIcon fontSize="small" />
                </IconButton>
            </UserProfileSection>

        </SidebarContainer>
    );
};

export default SideNavigationBar;
